import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarEventDialog = ({ open, onClose, onSuccess, event = null, selectedDate = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "event",
    start_time: "",
    end_time: "",
    location: "",
    meeting_link: "",
    invitees: [],
    external_invitees: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchUsers();
    if (event) {
      // Edit mode
      const startDate = new Date(event.start_time);
      const endDate = new Date(event.end_time);
      setFormData({
        title: event.title || "",
        description: event.description || "",
        event_type: event.event_type || "event",
        start_time: formatDateTimeLocal(startDate),
        end_time: formatDateTimeLocal(endDate),
        location: event.location || "",
        meeting_link: event.meeting_link || "",
        invitees: event.invitees || [],
        external_invitees: event.external_invitees ? event.external_invitees.join('; ') : "",
        priority: event.priority || "medium"
      });
    } else if (selectedDate) {
      // New event with pre-selected date
      const start = new Date(selectedDate);
      start.setHours(9, 0, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(10, 0, 0, 0);
      setFormData(prev => ({
        ...prev,
        start_time: formatDateTimeLocal(start),
        end_time: formatDateTimeLocal(end)
      }));
    }
  }, [event, selectedDate]);

  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data.filter(u => u.id !== user?.id)); // Exclude current user
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const toggleInvitee = (userId) => {
    setFormData(prev => ({
      ...prev,
      invitees: prev.invitees.includes(userId)
        ? prev.invitees.filter(id => id !== userId)
        : [...prev.invitees, userId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate end date is not before start date
    const startDate = new Date(formData.start_time);
    const endDate = new Date(formData.end_time);
    
    if (endDate < startDate) {
      toast.error("End date/time cannot be before start date/time");
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        event_type: formData.event_type,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        location: formData.location,
        meeting_link: formData.meeting_link || null,
        invitees: formData.invitees,
        external_invitees: formData.external_invitees || null,
        priority: formData.priority
      };

      if (event) {
        // Update existing event
        await axios.patch(`${API}/calendar/events/${event.id}`, payload, {
          withCredentials: true
        });
        toast.success("Event updated successfully");
      } else {
        // Create new event
        await axios.post(`${API}/calendar/events`, payload, {
          withCredentials: true
        });
        toast.success("Event created successfully");
      }
      
      onSuccess();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(error.response?.data?.detail || "Failed to save event");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this event?")) return;

    try {
      await axios.delete(`${API}/calendar/events/${event.id}`, {
        withCredentials: true
      });
      toast.success("Event deleted");
      onSuccess();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error(error.response?.data?.detail || "Failed to delete event");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{event ? "Edit Event" : "Create New Event"}</span>
            {event && event.creator_id === user?.id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Event title"
              required
              data-testid="event-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="event_type">Type</Label>
            <Select 
              value={formData.event_type} 
              onValueChange={(value) => setFormData({...formData, event_type: value})}
            >
              <SelectTrigger data-testid="event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select 
              value={formData.priority} 
              onValueChange={(value) => setFormData({...formData, priority: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Event description (optional)"
              rows={3}
              data-testid="event-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_time">Start Time *</Label>
              <Input
                id="start_time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                required
                data-testid="event-start"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time">End Time *</Label>
              <Input
                id="end_time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                required
                data-testid="event-end"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="Event location (optional)"
              data-testid="event-location"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="meeting_link">Virtual Meeting Room Link</Label>
            <Input
              id="meeting_link"
              type="url"
              value={formData.meeting_link}
              onChange={(e) => setFormData({...formData, meeting_link: e.target.value})}
              placeholder="https://zoom.us/j/... or https://meet.google.com/..."
              data-testid="event-meeting-link"
            />
            <p className="text-xs text-slate-500">Add Zoom, Google Meet, Teams, or other meeting link</p>
          </div>

          <div className="space-y-2">
            <Label>Invite People (Internal Team)</Label>
            <div className="border rounded-lg p-4 max-h-[180px] overflow-y-auto space-y-2">
              {users.length === 0 ? (
                <p className="text-sm text-slate-500">No other users available</p>
              ) : (
                users.map(u => (
                  <div key={u.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`user-${u.id}`}
                      checked={formData.invitees.includes(u.id)}
                      onCheckedChange={() => toggleInvitee(u.id)}
                    />
                    <label
                      htmlFor={`user-${u.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {u.first_name && u.last_name 
                        ? `${u.first_name} ${u.last_name}` 
                        : u.email}
                    </label>
                  </div>
                ))
              )}
            </div>
            {formData.invitees.length > 0 && (
              <p className="text-xs text-slate-500">
                {formData.invitees.length} team {formData.invitees.length === 1 ? "member" : "members"} invited
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="external_invitees">External Invitees (Emails)</Label>
            <Textarea
              id="external_invitees"
              value={formData.external_invitees}
              onChange={(e) => setFormData({...formData, external_invitees: e.target.value})}
              placeholder="john@example.com; jane@company.com; team@org.com"
              rows={2}
              data-testid="event-external-invitees"
            />
            <p className="text-xs text-slate-500">
              Separate multiple email addresses with semicolons (;)
            </p>
          </div>

          {event && event.meeting_link && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <Label className="text-sm font-semibold text-indigo-900 mb-2 block">Virtual Meeting</Label>
              <Button
                type="button"
                className="w-full"
                onClick={() => window.open(event.meeting_link, '_blank')}
              >
                Join Meeting Room
              </Button>
            </div>
          )}

          {event && event.creator_id !== user?.id && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                You can only view this event. Only the creator can edit or delete it.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {(!event || event.creator_id === user?.id) && (
              <Button type="submit" disabled={loading} data-testid="save-event">
                {loading ? "Saving..." : event ? "Update Event" : "Create Event"}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarEventDialog;
